import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useToast } from '@/design-system';
import { ns, useT } from '@/i18n';
import type { Message, Thread } from '@/services/domain';
import { useSession, useThreads } from '@/services/queries';
import { useChatLiveConnection } from '@/services/realtime/useChatLive';
import { paths } from '../paths';

const preview = (text: string) => (text.length > 60 ? `${text.slice(0, 57).trimEnd()}…` : text);

/**
 * The shell's live chat (P8): one connection per signed-in session, rooms of every conversation, and a
 * toast when a message arrives while the client is not reading that conversation (Decision 17).
 */
export function useLiveChat() {
  const { data: user } = useSession();
  const { data: threads } = useThreads(!!user);
  const toast = useToast();
  const t = useT(ns.shell);
  const location = useLocation();
  const where = useRef(location);
  useEffect(() => {
    where.current = location;
  }, [location]);

  const isViewing = useCallback((threadId: string) => {
    const { pathname, search } = where.current;
    return (
      pathname === paths.messages &&
      new URLSearchParams(search).get('thread') === threadId &&
      typeof document !== 'undefined' &&
      document.visibilityState !== 'hidden'
    );
  }, []);

  const onIncoming = useCallback(
    (thread: Thread, message: Message) =>
      toast.show(t('chat.toast', { name: thread.name, text: preview(message.text) })),
    [toast, t],
  );

  useChatLiveConnection({
    userId: user?.id ?? null,
    threadIds: (threads ?? []).map((t) => t.id),
    isViewing,
    onIncoming,
  });
}
